import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../utils/api";

interface VideoResponse {
  success: boolean;
  message: string;
  data?: {
    videoUrl?: string;
    boxFileId?: number;
    fileName?: string;
    mimeType: string;
    duration: number;
    resolution: string;
    style: string;
    persistError?: string;
  };
}

interface VideoGenerationState {
  loading: boolean;
  result: VideoResponse | null;
  error: string | null;
  videoUrl: string | null;
  boxFileId: number | null;
  fileName: string | null;
  saved: boolean;
}

export type VideoResolution = "1080p" | "720p";

interface GenerateVideoPayload {
  user_id: string;
  file: File;
  prompt?: string;
  smile_id?: string;
  resolution?: VideoResolution;
  durationSeconds?: 4 | 6 | 8;
  style?: string;
}

const initialState: VideoGenerationState = {
  loading: false,
  result: null,
  error: null,
  videoUrl: null,
  boxFileId: null,
  fileName: null,
  saved: false,
};

export const generateVideo = createAsyncThunk<
  VideoResponse,
  GenerateVideoPayload,
  { rejectValue: string }
>("videoGeneration/generateVideo", async (payload, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append("user_id", payload.user_id);
    formData.append("afterImage", payload.file, payload.file.name);
    if (payload.prompt) formData.append("prompt", payload.prompt);
    if (payload.smile_id) formData.append("smile_id", payload.smile_id);
    formData.append("resolution", payload.resolution ?? "720p");
    if (payload.durationSeconds != null) {
      formData.append("durationSeconds", String(payload.durationSeconds));
    }
    if (payload.style) formData.append("style", payload.style);

    const response = await api.post<VideoResponse>(
      "/smile-design/smile-video",
      formData,
      {
        timeout: 660_000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      }
    );

    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Video generation failed";
    return rejectWithValue(message);
  }
});

const videoGenerationSlice = createSlice({
  name: "videoGeneration",
  initialState,
  reducers: {
    resetVideoState(state) {
      state.loading = false;
      state.result = null;
      state.error = null;
      state.videoUrl = null;
      state.boxFileId = null;
      state.fileName = null;
      state.saved = false;
    },
    setVideoPlaybackUrl(state, action: PayloadAction<string | null>) {
      state.videoUrl = action.payload;
    },
    setCaseVideo(
      state,
      action: PayloadAction<{ boxFileId: number; fileName?: string | null }>
    ) {
      state.loading = false;
      state.error = null;
      state.result = null;
      state.videoUrl = null;
      state.boxFileId = action.payload.boxFileId;
      state.fileName = action.payload.fileName ?? null;
      state.saved = true;
    },
    markVideoSaved(
      state,
      action: PayloadAction<{ boxFileId: number; fileName?: string | null }>
    ) {
      state.boxFileId = action.payload.boxFileId;
      state.fileName = action.payload.fileName ?? state.fileName;
      state.saved = true;
    },
    clearVideoError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateVideo.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.result = null;
        state.videoUrl = null;
        state.boxFileId = null;
        state.fileName = null;
        state.saved = false;
      })
      .addCase(generateVideo.fulfilled, (state, action: PayloadAction<VideoResponse>) => {
        state.loading = false;
        state.result = action.payload;
        state.boxFileId = action.payload.data?.boxFileId ?? null;
        state.fileName = action.payload.data?.fileName ?? null;
        state.videoUrl = action.payload.data?.videoUrl ?? null;
        state.saved = !!action.payload.data?.boxFileId;
        if (action.payload.data?.persistError) {
          state.saved = false;
          state.error = action.payload.data.persistError;
        }
      })
      .addCase(generateVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Video generation failed";
      });
  },
});

export const { resetVideoState, clearVideoError, setVideoPlaybackUrl, setCaseVideo, markVideoSaved } =
  videoGenerationSlice.actions;
export default videoGenerationSlice.reducer;
